import {
  getSupportMessageCounter,
  getUnreadMessagesCount,
} from '@/api/messages';
import { getUnreadNotificationsCount } from '@/api/notifications';
import { useApiQuery } from '@/hooks/api';
import { LISTING_STALE_TIME } from '@/hooks/api/defaults';
import { queryKeys } from '@/shared/lib/queryKeys';

const UNREAD_POLL_MS = 60_000;

function useUnreadMessagesCount(options?: { enabled?: boolean }) {
  return useApiQuery<number>({
    queryKey: queryKeys.messages.unreadGlobal(),
    queryFn: async () => {
      const res = await getUnreadMessagesCount();
      return res.unread_message_cnt;
    },
    staleTime: LISTING_STALE_TIME,
    refetchInterval: UNREAD_POLL_MS,
    enabled: options?.enabled ?? true,
  });
}

export function useUnreadNotificationsCount(options?: { enabled?: boolean }) {
  return useApiQuery<number>({
    queryKey: queryKeys.notifications.unread(),
    queryFn: async () => {
      const res = await getUnreadNotificationsCount();
      return res.unread_notifications_cnt;
    },
    staleTime: LISTING_STALE_TIME,
    refetchInterval: UNREAD_POLL_MS,
    enabled: options?.enabled ?? true,
  });
}

export function useSupportUnreadCount(options?: { enabled?: boolean }) {
  return useApiQuery<number>({
    queryKey: queryKeys.messages.unreadSupport(),
    queryFn: async () => {
      const res = await getSupportMessageCounter();
      return res.unread_message_cnt;
    },
    staleTime: LISTING_STALE_TIME,
    refetchInterval: UNREAD_POLL_MS,
    enabled: options?.enabled ?? true,
  });
}

/** Combined unread badges for navigation — single poll interval via shared query keys. */
export function useUnreadCounts(options?: { enabled?: boolean }) {
  const messages = useUnreadMessagesCount(options);
  const notifications = useUnreadNotificationsCount(options);

  return {
    globalUnread: messages.data ?? 0,
    notificationsUnread: notifications.data ?? 0,
    isLoading: messages.isLoading || notifications.isLoading,
    refetch: () => {
      void messages.refetch();
      void notifications.refetch();
    },
  };
}
